import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RecurringSchedule, RecurringScheduleProviderViewModel } from '../../redux/API/ClientAPIHelpers/recurringSchedule';
import { errorNormalizer } from '../../shared/Helpers/functions';

export interface ProviderSearchOption {
	id: string;
	label: string;
}

const DEBOUNCE_MS = 300;
const INITIAL_PAGE_SIZE = 20;

const toOption = (provider: RecurringScheduleProviderViewModel): ProviderSearchOption => ({
	id: provider.id,
	label: provider.fullName,
});

export const useProviderSearch = (includeEnded = false) => {
	const navigate = useNavigate();
	const [providerSearch, setProviderSearch] = useState('');
	const [providerOptions, setProviderOptions] = useState<ProviderSearchOption[]>([]);
	const [providerPageSize, setProviderPageSize] = useState(INITIAL_PAGE_SIZE);
	const [providerHasMore, setProviderHasMore] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();

	const handleSetProviderSearch = (value: string) => {
		setProviderSearch(value || '');
		setProviderPageSize(INITIAL_PAGE_SIZE);
	};

	useEffect(() => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			RecurringSchedule.getScheduledProviders({
				page: '1',
				pageSize: String(providerPageSize),
				includeEnded,
				searchString: providerSearch || '',
			})
				.then((response) => {
					if (response?.error?.status === 403) {
						navigate('/dashboard');

						return;
					}

					const list: RecurringScheduleProviderViewModel[] = response.query || [];

					setProviderOptions(list.map(toOption));
					setProviderHasMore(Boolean(response.hasNextPage));
				})
				.catch(errorNormalizer);
		}, DEBOUNCE_MS);

		return () => clearTimeout(debounceRef.current);
	}, [includeEnded, providerPageSize, providerSearch, navigate]);

	return {
		providerHasMore,
		providerOptions,
		providerPageSize,
		providerSearch,
		setProviderPageSize,
		setProviderSearch: handleSetProviderSearch,
	};
};
